import { useCallback, useState } from 'react';
import { Box, Button, Checkbox, Typography, debounce } from '@mui/material';
import CustomInputField from 'src/components/CustomInputField';

import { closeBottomSheet } from 'src/components/BottomSheet';

import BottomSheetHeader from '../BottomSheetHeader';

interface SearchCityProps {
    activeCity?: string;
    cities: string[];
    onSubmit: (cities: string[]) => void;
}

const EmptyResult = () => {
    return (
        <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            flexGrow={10}
        >
            <Box my={5}>
                <Typography
                    variant="body1"
                    color="text.primary"
                    textAlign="center"
                >
                    Поиск не дал результатов...
                </Typography>
            </Box>
        </Box>
    );
};

export default function SearchCity({ cities, onSubmit, activeCity }: SearchCityProps) {
    // Сортируем города по алфавиту
    const sortedCities = [...cities].sort((a, b) => a.localeCompare(b, 'ru'));
    const [searchVal, setSearchVal] = useState('');
    const [filteredCities, setFilteredCities] = useState(sortedCities);
    const [selectedCities, setSelectedCities] = useState<string[]>(activeCity ? [activeCity] : []);

    const handleCityToggle = (city: string) => {
        setSelectedCities((prev) => {
            if (prev.includes(city)) {
                return prev.filter((c) => c !== city);
            } else {
                return [...prev, city];
            }
        });
    };

    const handleSubmit = () => {
        onSubmit(selectedCities);
        closeBottomSheet();
    };

    const onSearchChange = (value: string) => {
        setSearchVal(value);
        onFilteredCitiesChange(value);
    };

    const onFilterCitiesChangeHandler = (value: string) => {
        if (!value) {
            setFilteredCities(sortedCities);
            return;
        }
        setFilteredCities(sortedCities.filter((city) => city.toLowerCase().includes(value.trim().toLowerCase())));
    };

    const onFilteredCitiesChange = useCallback(debounce(onFilterCitiesChangeHandler, 300), []);

    return (
        <Box
            px={1}
            position="relative"
        >
            <Box mb={2}>
                <BottomSheetHeader title="Локации" />
            </Box>
            <Box mb={1}>
                <CustomInputField
                    value={searchVal}
                    onChange={onSearchChange}
                    placeholder="Поиск"
                />
            </Box>
            <Box
                border="1px solid var(--lm-border)"
                borderRadius={4}
                height={'200px'}
                overflow="auto"
            >
                {filteredCities.length > 0 ? (
                    <>
                        {filteredCities.map((city, idx) => (
                            <Box
                                pl={2}
                                pr={1.5}
                                py={1}
                                component="label"
                                htmlFor={city}
                                key={city}
                                style={{ cursor: 'pointer' }}
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                                borderBottom={idx !== filteredCities.length - 1 ? '1px solid var(--lm-border)' : 'none'}
                            >
                                <Typography
                                    variant="body1"
                                    color="text.primary"
                                >
                                    {city}
                                </Typography>
                                <Checkbox
                                    id={city}
                                    checked={selectedCities.includes(city)}
                                    onChange={() => handleCityToggle(city)}
                                />
                            </Box>
                        ))}
                    </>
                ) : (
                    <EmptyResult />
                )}
            </Box>
            <Box
                mb={1}
                display="flex"
                gap={1}
            >
                {selectedCities.length > 0 && (
                    <Button
                        onClick={() => {
                            setSelectedCities([]);
                            onSubmit([]);
                            closeBottomSheet();
                        }}
                        variant="outlined"
                        size="large"
                        sx={{ flex: 1 }}
                    >
                        Показать все
                    </Button>
                )}
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    size="large"
                    sx={{ flex: 1 }}
                >
                    {selectedCities.length > 0 ? `Выбрать (${selectedCities.length})` : 'Показать все'}
                </Button>
            </Box>
        </Box>
    );
}
